import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Spinner, Container, Row, Col, ListGroup } from "react-bootstrap";

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  return params.get("q") || "";
};

const sheets = {
  courses: "Courses",
  sessions: "ZoomSessions",
  readings: "ReadingMaterials",
};

const SHEET_BASE = "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU";

const SearchResults = () => {
  const location = useLocation();
  const query = parseQuery(location.search).toLowerCase();
  const [data, setData] = useState({ courses: [], sessions: [], readings: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      // 1. Fetch courses and create CourseId → CourseName map
      const coursesRes = await fetch(`${SHEET_BASE}/${sheets.courses}`);
      const coursesJson = await coursesRes.json();
      const courseIdToName = {};
      coursesJson.forEach(course => {
        courseIdToName[course.CourseId] = course.CourseName;
      });

      // 2. Filter courses as usual
      const filteredCourses = coursesJson.filter(item =>
        Object.values(item).some(val => val && val.toLowerCase().includes(query))
      );

      // 3. Fetch ZoomSessions and enrich with CourseName
      const sessionsRes = await fetch(`${SHEET_BASE}/${sheets.sessions}`);
      const sessionsJson = await sessionsRes.json();
      const enrichedSessions = sessionsJson.map(session => ({
        ...session,
        CourseName: courseIdToName[session.CourseId] || "",
      }));
      // Filter sessions including CourseName in search
      const filteredSessions = enrichedSessions.filter(session =>
        Object.values(session).some(val => val && val.toLowerCase().includes(query))
      );

      // 4. Fetch and filter reading materials
      const readingsRes = await fetch(`${SHEET_BASE}/${sheets.readings}`);
      const readingsJson = await readingsRes.json();
      const filteredReadings = readingsJson.filter(item =>
        Object.values(item).some(val => val && val.toLowerCase().includes(query))
      );

      setData({
        courses: filteredCourses,
        sessions: filteredSessions,
        readings: filteredReadings,
      });
      setLoading(false);
    };

    fetchAll();
  }, [query]);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-50">
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );

  return (
    <Container className="my-4">
      <h2 className="mb-4">
        Search Results for: <em className="text-primary">{query}</em>
      </h2>

      <Row className="mb-5">
        <Col md={4}>
          <h4 className="mb-3 border-bottom pb-2">Courses</h4>
          {data.courses.length === 0 ? (
            <p className="text-muted">No matching courses.</p>
          ) : (
            <ListGroup>
              {data.courses.map((course) => (
                <ListGroup.Item
                  key={course.CourseId}
                  action
                  as={Link}
                  to={`/course/${course.CourseId}`}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span>{course.CourseName}</span>
                  <small className="text-muted">{course.Faculty}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>

        <Col md={4}>
          <h4 className="mb-3 border-bottom pb-2">Sessions</h4>
          {data.sessions.length === 0 ? (
            <p className="text-muted">No matching sessions.</p>
          ) : (
            <ListGroup>
              {data.sessions.map((s, i) => (
                <ListGroup.Item key={i} className="d-flex flex-column">
                  <strong>{s.Class}</strong>
                  <small className="text-muted">
                    {s.Date} — <em>{s.CourseName}</em>
                  </small>
                  <a
                    href={s["Meeting Link"]}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1"
                  >
                    Zoom Link
                  </a>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>

        <Col md={4}>
          <h4 className="mb-3 border-bottom pb-2">Reading Materials</h4>
          {data.readings.length === 0 ? (
            <p className="text-muted">No matching readings.</p>
          ) : (
            <ListGroup>
              {data.readings.map((r, i) => (
                <ListGroup.Item
                  key={i}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>{r.Title}</strong> <br />
                    <small className="text-muted">{r.CourseId}</small>
                  </div>
                  <a
                    href={r.ReadingMaterialLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    View
                  </a>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SearchResults;
